import { Eye, EyeOff, LogIn, Mail, UserPlus } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useAuth } from './auth'
import { Campo } from './Campo'
import { tc } from './idioma'

type Modo = 'ingresar' | 'registro' | 'olvide'

/** La misma regla que fija Supabase Auth: 10 o más caracteres con minúscula, mayúscula y número. */
export function validarPassword(valor: string): string | null {
  if (valor.length < 10) return tc().passwordCorta
  if (!/[a-z]/.test(valor) || !/[A-Z]/.test(valor) || !/[0-9]/.test(valor)) return tc().passwordMezcla
  return null
}

/** Campo de contraseña con botón para mostrar u ocultar lo escrito. */
export function CampoPassword({
  etiqueta,
  valor,
  alCambiar,
  nueva = false,
  error,
  ayuda,
}: {
  etiqueta: string
  valor: string
  alCambiar: (v: string) => void
  nueva?: boolean
  error?: string
  ayuda?: string
}) {
  const [visible, setVisible] = useState(false)
  return (
    <Campo etiqueta={etiqueta} error={error} ayuda={ayuda}>
      {(c) => (
        <div className="campo__password">
          <input {...c} type={visible ? 'text' : 'password'} autoComplete={nueva ? 'new-password' : 'current-password'} required value={valor} onChange={(e) => alCambiar(e.target.value)} />
          <button type="button" className="campo__ver" onClick={() => setVisible(!visible)} aria-pressed={visible} aria-label={visible ? tc().ocultar : tc().mostrar}>
            {visible ? <EyeOff className="icono" strokeWidth={1.75} aria-hidden="true" /> : <Eye className="icono" strokeWidth={1.75} aria-hidden="true" />}
          </button>
        </div>
      )}
    </Campo>
  )
}

type Props = {
  /** La tienda deja que cualquiera cree su cuenta; el portal no: su personal lo da de alta el administrador. */
  permitirRegistro?: boolean
}

type Fase = 'libre' | 'enviando' | 'aviso' | 'error'

/**
 * Acceso con correo y contraseña, más «Olvidé mi contraseña» y, si se permite, crear una cuenta. Los mensajes de
 * error no revelan si un correo existe (salvo al registrarse, donde hay que avisar que ya está en uso).
 */
export function FormularioPassword({ permitirRegistro = false }: Props) {
  const { cliente } = useAuth()
  const t = tc()
  const [modo, setModo] = useState<Modo>('ingresar')
  const [correo, setCorreo] = useState('')
  const [password, setPassword] = useState('')
  const [fase, setFase] = useState<Fase>('libre')
  const [mensaje, setMensaje] = useState<string | null>(null)
  const [errorPassword, setErrorPassword] = useState<string | undefined>()

  function cambiarModo(m: Modo) {
    setModo(m)
    setFase('libre')
    setMensaje(null)
    setErrorPassword(undefined)
  }

  async function enviar(e: FormEvent) {
    e.preventDefault()
    if (!cliente || fase === 'enviando') return
    const email = correo.trim().toLowerCase()
    setMensaje(null)
    setErrorPassword(undefined)

    if (modo === 'registro') {
      const problema = validarPassword(password)
      if (problema) return setErrorPassword(problema)
    }
    setFase('enviando')

    if (modo === 'ingresar') {
      const { error } = await cliente.auth.signInWithPassword({ email, password })
      if (error) {
        setFase('error')
        setMensaje(error.code === 'email_not_confirmed' ? t.sinConfirmar : error.status === 429 ? t.demasiados : t.credenciales)
        return
      }
      setFase('libre') // la sesión llega por onAuthStateChange y la pantalla cambia sola
      return
    }

    if (modo === 'registro') {
      const { data, error } = await cliente.auth.signUp({ email, password, options: { emailRedirectTo: window.location.origin } })
      if (error) {
        setFase('error')
        setMensaje(error.code === 'user_already_exists' ? t.yaExiste : error.code === 'weak_password' ? t.debil : error.status === 429 ? t.demasiados : t.noCreada)
        return
      }
      if (data.session) return setFase('libre') // entra directo
      setFase('aviso')
      setMensaje(t.confirmar)
      return
    }

    // olvide
    const { error } = await cliente.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin })
    if (error && (error.status === 429 || error.status === undefined || error.status >= 500)) {
      setFase('error')
      setMensaje(t.noEnviado)
      return
    }
    setFase('aviso')
    setMensaje(t.enlaceEnviado)
  }

  const titulos: Record<Modo, string> = { ingresar: t.ingresar, registro: t.crearCuenta, olvide: t.enviarEnlace }
  const Icono = modo === 'registro' ? UserPlus : modo === 'olvide' ? Mail : LogIn
  const enviando = fase === 'enviando'

  if (fase === 'aviso') {
    return (
      <div className="aviso-vacio" role="status">
        <p>{mensaje}</p>
        <p className="accion">
          <button type="button" className="boton boton--secundario" onClick={() => cambiarModo('ingresar')}>
            {t.volverIngresar}
          </button>
        </p>
      </div>
    )
  }

  return (
    <form className="formulario" onSubmit={(e) => void enviar(e)} noValidate={false}>
      <Campo etiqueta={t.correo}>
        {(c) => <input {...c} type="email" name="email" autoComplete="email" inputMode="email" required value={correo} onChange={(e) => setCorreo(e.target.value)} />}
      </Campo>
      {modo !== 'olvide' && (
        <CampoPassword
          etiqueta={t.password}
          valor={password}
          alCambiar={setPassword}
          nueva={modo === 'registro'}
          error={errorPassword}
          ayuda={modo === 'registro' ? t.ayudaPassword : undefined}
        />
      )}
      {fase === 'error' && mensaje && (
        <p className="campo__error" role="alert">
          {mensaje}
        </p>
      )}
      <button type="submit" className="boton boton--primario" disabled={enviando} aria-busy={enviando}>
        <Icono className="icono" strokeWidth={1.75} aria-hidden="true" />
        {enviando ? t.unMomento : titulos[modo]}
      </button>
      <div className="acceso__enlaces">
        {modo === 'ingresar' && (
          <button type="button" className="boton boton--terciario boton--compacto" onClick={() => cambiarModo('olvide')}>
            {t.olvide}
          </button>
        )}
        {modo === 'ingresar' && permitirRegistro && (
          <button type="button" className="boton boton--terciario boton--compacto" onClick={() => cambiarModo('registro')}>
            {t.crearUna}
          </button>
        )}
        {modo !== 'ingresar' && (
          <button type="button" className="boton boton--terciario boton--compacto" onClick={() => cambiarModo('ingresar')}>
            {t.yaTengo}
          </button>
        )}
      </div>
    </form>
  )
}

/** Pantalla para elegir una contraseña nueva tras abrir el enlace de «Olvidé mi contraseña». */
export function NuevaPassword() {
  const { cliente, terminarRecuperacion } = useAuth()
  const t = tc()
  const [password, setPassword] = useState('')
  const [repetir, setRepetir] = useState('')
  const [errores, setErrores] = useState<{ password?: string; repetir?: string; general?: string }>({})
  const [guardando, setGuardando] = useState(false)

  async function guardar(e: FormEvent) {
    e.preventDefault()
    if (!cliente || guardando) return
    const problema = validarPassword(password)
    if (problema) return setErrores({ password: problema })
    if (password !== repetir) return setErrores({ repetir: t.noCoinciden })
    setErrores({})
    setGuardando(true)
    const { error } = await cliente.auth.updateUser({ password })
    setGuardando(false)
    if (error) {
      return setErrores({ general: error.code === 'same_password' ? t.distinta : error.code === 'weak_password' ? t.debil : t.noGuardada })
    }
    terminarRecuperacion()
  }

  return (
    <form className="formulario" onSubmit={(e) => void guardar(e)}>
      <CampoPassword etiqueta={t.nueva} valor={password} alCambiar={setPassword} nueva error={errores.password} ayuda={t.ayudaPassword} />
      <CampoPassword etiqueta={t.repetir} valor={repetir} alCambiar={setRepetir} nueva error={errores.repetir} />
      {errores.general && (
        <p className="campo__error" role="alert">
          {errores.general}
        </p>
      )}
      <button type="submit" className="boton boton--primario" disabled={guardando} aria-busy={guardando}>
        {guardando ? t.guardando : t.guardar}
      </button>
    </form>
  )
}
